/**
 * Chart templates (owner 2026-09-28; TradingView "Save chart template"): the pane openers for the
 * palette / right-click menu / toolbar. The synced document and the default template's seed for new
 * charts are in chartTemplatesDoc.ts (they need the indicator registry, which loads with the charts).
 *
 * Every chart pane registers a Templates opener under its `chartKey` (`${panelId}/${paneId}`), like
 * chart settings (chartSettings.ts); `openChartTemplates(paneKey?, mode)` opens the pane's template
 * menu ("apply") or its save dialog ("save"), the most recently used pane without a key.
 */

export type ChartTemplatesMode = "apply" | "save";
type Opener = (mode: ChartTemplatesMode, at?: { x: number; y: number }) => void;

const openers = new Map<string, Opener>();
/** Pane keys, most recently used last. */
const recent: string[] = [];

function touch(key: string): void {
  const i = recent.indexOf(key);
  if (i >= 0) recent.splice(i, 1);
  recent.push(key);
}

/** A chart pane offers its templates menu (ChartPanel); returns the unregister function. */
export function registerChartTemplatesTarget(paneKey: string, open: Opener): () => void {
  openers.set(paneKey, open);
  if (!recent.includes(paneKey)) recent.unshift(paneKey);
  return () => {
    if (openers.get(paneKey) !== open) return;
    openers.delete(paneKey);
    const i = recent.indexOf(paneKey);
    if (i >= 0) recent.splice(i, 1);
  };
}

/** The pane was used (clicked / made active): the palette commands target it next. */
export function markChartTemplatesTarget(paneKey: string): void {
  if (openers.has(paneKey)) touch(paneKey);
}

/** Chart panes that can open templates (palette `when`). */
export function chartTemplatesTargets(): number {
  return openers.size;
}

/**
 * Open pane `paneKey`'s template menu (`mode` "apply") or save dialog ("save"), at `at` (client
 * coordinates, the right-click menu) or under the pane's button; without a key the most recently
 * used pane. False when no such pane is mounted.
 */
export function openChartTemplates(
  paneKey: string | null | undefined,
  mode: ChartTemplatesMode,
  at?: { x: number; y: number },
): boolean {
  const key = paneKey ?? recent.at(-1);
  const open = key === undefined ? undefined : openers.get(key);
  if (!key || !open) return false;
  touch(key);
  open(mode, at);
  return true;
}
